// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 68fb6015-f7c0-4b3f-8032-07769ee6b395

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Divider, Grid, MenuItem, Paper, Stack, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import InsightsIcon from '@mui/icons-material/Insights';
import LockIcon from '@mui/icons-material/Lock';
import ShieldIcon from '@mui/icons-material/Shield';
import DownloadIcon from '@mui/icons-material/Download';
import { PICKLISTS } from './schema';
import { isMaster } from './auth';
import { getConfig } from './config';
import { exportCsv } from './exports';
import store from './store';
import HelpBubble from './HelpBubble';
import { TabLabel } from './ui';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

// ---------------------------------------------------------------------------
// Benchmarks — Optimum's cross-client pattern knowledge, turned into guidance.
//
// Optimum has run cloud-migration discovery across many health systems. That
// accumulated pattern knowledge ("clinical apps are usually Rehost", "this
// vendor is typically SaaS-ready", "Tier 1 apps land on an 8-hour RTO") is the
// one thing a client cannot buy from LeanIX directly. This screen turns the
// corpus into numbers a consultant can quote in a workshop.
//
// PRIVACY IS THE DESIGN, NOT A FILTER ON TOP OF IT:
//   1. Master-only. A client user never reaches this screen; they get an Alert.
//   2. Aggregates only. Nothing here reads a name, an owner, an IP, a URL or
//      any other row-level field — only the classification/picklist columns are
//      counted, and only ever as counts.
//   3. Minimum cohort. Every statistic must be backed by >= MIN distinct client
//      tenants (default 3). Below that it is withheld and labelled "insufficient
//      data" — because with two clients an average is trivially invertible, and
//      with one it is simply that client's data wearing a moustache.
//   4. Cohort size is printed next to every number, so a thin sample can never
//      be mistaken for a trend.
// ---------------------------------------------------------------------------

// Optimum palette.
const HEAD = 'sectionHeader.main';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';

// Stack colours for the distribution charts.
const SERIES = ['#0D5B9A', '#2E7D32', '#F5A623', '#8E24AA', '#00838F', '#C62828', '#5C6B7A', '#3D9AD1'];

const MASTER_TENANT = 'optimum';
const MIN_COHORT_OPTIONS = [3, 4, 5, 6];

const norm = (v) => (typeof v === 'string' ? v.trim() : v);
const has = (v) => v !== undefined && v !== null && String(v).trim() !== '';
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// ---------------------------------------------------------------------------
// Dimension / measure accessors. Every accessor below reads a picklist-style
// classification field. None of them read a free-text identifier.
// ---------------------------------------------------------------------------

// DR tier lives either in the field or in the "DR Tier" tag group, where the
// imported values already carry their own prefix ("DR Tier: 2").
const drTierOf = (a) => {
  const direct = norm(a.fields?.drTier);
  if (has(direct)) return direct;
  const tagged = (a.tags || []).find((t) => t.group === 'DR Tier')?.value;
  if (!has(tagged)) return '';
  const m = String(tagged).match(/[1-4]/);
  return m ? `Tier ${m[0]}` : String(tagged).trim();
};

const appTypeOf = (a) => {
  const direct = norm(a.fields?.applicationType);
  if (has(direct)) return direct;
  return norm((a.tags || []).find((t) => t.group === 'Cell')?.value) || '';
};

const vendorOf = (a) => norm(a.fields?.vendorName) || '';
const hostingOf = (a) => norm(a.fields?.hostingType) || '';
// 6R is captured in the portfolio section and again inside Cloud Migration
// General; either counts as "the disposition we landed on".
const sixROf = (a) => norm(a.fields?.sixRClassification) || norm(a.fields?.cmDisposition) || '';
const timeOf = (a) => norm(a.fields?.timeClassification) || '';
const rtoOf = (a) => norm(a.fields?.rto) || '';
const rpoOf = (a) => norm(a.fields?.rpo) || '';
const ssoOf = (a) => norm(a.fields?.ssoAvailability) || '';

const DIMENSIONS = [
  { key: 'applicationType', label: 'Application type', of: appTypeOf },
  { key: 'vendorName', label: 'Vendor', of: vendorOf },
  { key: 'hostingType', label: 'Hosting type', of: hostingOf },
  { key: 'drTier', label: 'DR tier', of: drTierOf },
];

const MEASURES = [
  { key: 'sixR', label: '6R disposition', of: sixROf, options: PICKLISTS.sixR },
  { key: 'time', label: 'TIME classification', of: timeOf, options: PICKLISTS.time },
];

// ---------------------------------------------------------------------------
// Aggregation primitives. Each returns { value, apps, clients, suppressed } so
// the renderer never has to decide on its own whether something may be shown.
// ---------------------------------------------------------------------------

// Cross-tab: dimension value x measure value, counted by app AND by distinct
// client tenant. The tenant counts are what drive suppression.
function buildCrosstab(rows, dimOf, measOf, options, minCohort) {
  const groups = new Map();
  rows.forEach((r) => {
    const g = norm(dimOf(r));
    const m = norm(measOf(r));
    if (!has(g) || !has(m)) return;
    if (!groups.has(g)) groups.set(g, { key: g, apps: 0, tenants: new Set(), cells: new Map() });
    const grp = groups.get(g);
    grp.apps += 1;
    grp.tenants.add(r.tenantId);
    if (!grp.cells.has(m)) grp.cells.set(m, { value: m, apps: 0, tenants: new Set() });
    const cell = grp.cells.get(m);
    cell.apps += 1;
    cell.tenants.add(r.tenantId);
  });

  const rank = (v) => {
    const i = options.indexOf(v);
    return i < 0 ? 99 : i;
  };

  return [...groups.values()]
    .map((g) => ({
      key: g.key,
      apps: g.apps,
      clients: g.tenants.size,
      suppressed: g.tenants.size < minCohort,
      cells: [...g.cells.values()]
        .map((c) => ({
          value: c.value,
          apps: c.apps,
          clients: c.tenants.size,
          pct: Math.round((c.apps / g.apps) * 100),
          // A cell is withheld if too few clients contributed to THAT cell,
          // even when the group as a whole clears the bar.
          suppressed: c.tenants.size < minCohort,
        }))
        .sort((a, b) => b.apps - a.apps || rank(a.value) - rank(b.value)),
    }))
    .sort((a, b) => b.apps - a.apps || a.key.localeCompare(b.key));
}

// Median over an ordinal picklist (RTO/RPO are buckets, not numbers, so the
// median is the middle bucket — the lower one on an even count).
function medianOrdinal(values, options) {
  const idx = values.map((v) => options.indexOf(v)).filter((i) => i >= 0).sort((a, b) => a - b);
  if (!idx.length) return null;
  return options[idx[Math.floor((idx.length - 1) / 2)]];
}

function modeOf(values) {
  const counts = new Map();
  values.forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
  let best = null;
  let bestN = 0;
  counts.forEach((n, v) => {
    if (n > bestN) { best = v; bestN = n; }
  });
  return best === null ? null : { value: best, share: Math.round((bestN / values.length) * 100) };
}

// The one place a statistic is allowed to become visible.
function statFor(rows, valueOf, { kind = 'mode', options = [], minCohort }) {
  const contributing = rows.filter((r) => has(valueOf(r)));
  const clients = new Set(contributing.map((r) => r.tenantId)).size;
  const base = { apps: contributing.length, clients };
  if (clients < minCohort) return { ...base, suppressed: true, value: null };
  const values = contributing.map((r) => norm(valueOf(r)));
  if (kind === 'median') {
    return { ...base, suppressed: false, value: medianOrdinal(values, options) };
  }
  const m = modeOf(values);
  return { ...base, suppressed: false, value: m ? m.value : null, share: m ? m.share : null };
}

// Rate statistic (SSO availability): share of apps whose captured value matches.
function rateFor(rows, valueOf, matches, minCohort) {
  const contributing = rows.filter((r) => has(valueOf(r)));
  const clients = new Set(contributing.map((r) => r.tenantId)).size;
  const base = { apps: contributing.length, clients };
  if (clients < minCohort) return { ...base, suppressed: true, value: null };
  const hits = contributing.filter((r) => matches(norm(valueOf(r)))).length;
  return { ...base, suppressed: false, value: Math.round((hits / contributing.length) * 100) };
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

function Cohort({ clients, apps }) {
  return (
    <Typography component="span" variant="caption" sx={{ color: GREY, whiteSpace: 'nowrap' }}>
      {`n = ${plural(clients, 'client')}, ${plural(apps, 'app')}`}
    </Typography>
  );
}

function Insufficient({ clients, minCohort }) {
  return (
    <Tooltip
      title={`Backed by ${plural(clients, 'client')} — below the ${minCohort}-client minimum. Publishing it would let you work backwards to an individual client's data, so the value is withheld.`}
    >
      <Chip
        size="small"
        icon={<LockIcon sx={{ fontSize: 14 }} />}
        label="insufficient data"
        sx={{ bgcolor: '#f1f3f6', color: GREY, fontWeight: 600 }}
      />
    </Tooltip>
  );
}

// One statistic + its cohort, or the suppression chip. Nothing else may render
// a number in this file.
function Stat({ stat, minCohort, format }) {
  if (!stat || stat.value === null || stat.value === undefined) {
    if (stat && stat.suppressed) return <Insufficient clients={stat.clients} minCohort={minCohort} />;
    return <span style={{ color: '#aaa' }}>—</span>;
  }
  if (stat.suppressed) return <Insufficient clients={stat.clients} minCohort={minCohort} />;
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: HEAD }}>
        {format ? format(stat) : String(stat.value)}
      </Typography>
      <Cohort clients={stat.clients} apps={stat.apps} />
    </Stack>
  );
}

function SectionHeading({ icon, title, subtitle }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1.5 }}>
      {icon}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>{title}</Typography>
        {subtitle && <Typography variant="body2" sx={{ color: GREY }}>{subtitle}</Typography>}
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// The panel Frank reads out when a client asks "what do you do with our data?"
// ---------------------------------------------------------------------------
function PrivacyPanel({ minCohort, clients, apps }) {
  return (
    <Paper sx={{ p: 2, mb: 2, borderLeft: `4px solid ${BLUE}` }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ShieldIcon sx={{ color: 'sectionHeader.main' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>
          What is and is not shared across clients
        </Typography>
        <Chip size="small" label="Optimum-internal view" sx={{ bgcolor: '#eef3ff', color: BLUE, fontWeight: 600 }} />
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="overline" sx={{ color: GREY }}>Shared into benchmarks</Typography>
          <Box component="ul" sx={{ margin: '4px 0 0 18px', padding: 0, color: HEAD, fontSize: 14, lineHeight: 1.7 }}>
            <li>Counts and percentages of picklist values only — 6R, TIME, hosting, DR tier, RTO, RPO, SSO availability, data completion.</li>
            <li>Every figure is pooled across at least {minCohort} distinct client workspaces before it is shown.</li>
            <li>Cohort size is printed beside every number: n = clients, applications.</li>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="overline" sx={{ color: GREY }}>Never shared, never rendered here</Typography>
          <Box component="ul" sx={{ margin: '4px 0 0 18px', padding: 0, color: HEAD, fontSize: 14, lineHeight: 1.7 }}>
            <li>No application names, owners, stakeholders, servers, IPs, URLs or any other row-level record.</li>
            <li>No client identification — no workspace is named, ranked or attributed anywhere on this screen.</li>
            <li>Nothing derived from fewer than {minCohort} clients. With two clients an average reveals both, so it is withheld and marked &ldquo;insufficient data&rdquo;.</li>
          </Box>
        </Grid>
      </Grid>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="body2" sx={{ color: GREY }}>
        Current corpus: {plural(clients, 'client workspace')}, {plural(apps, 'application')}. Client users cannot open this
        screen at all — benchmarks are visible only to Optimum master administrators.
      </Typography>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// TAB 1 — Patterns: how a dimension distributes across dispositions.
// ---------------------------------------------------------------------------
function PatternsTab({ rows, minCohort }) {
  const [dimKey, setDimKey] = useState('applicationType');
  const [measKey, setMeasKey] = useState('sixR');

  const dim = DIMENSIONS.find((d) => d.key === dimKey) || DIMENSIONS[0];
  const meas = MEASURES.find((m) => m.key === measKey) || MEASURES[0];

  const table = useMemo(
    () => buildCrosstab(rows, dim.of, meas.of, meas.options, minCohort),
    [rows, dim, meas, minCohort]
  );

  const visible = useMemo(() => table.filter((g) => !g.suppressed).slice(0, 12), [table]);
  const withheld = useMemo(() => table.filter((g) => g.suppressed), [table]);

  const columns = useMemo(() => {
    const found = new Set();
    table.forEach((g) => g.cells.forEach((c) => found.add(c.value)));
    return [
      ...meas.options.filter((o) => found.has(o)),
      ...[...found].filter((v) => !meas.options.includes(v)),
    ];
  }, [table, meas]);

  const chart = useMemo(() => ({
    labels: visible.map((g) => g.key),
    datasets: columns.map((col, i) => ({
      label: col,
      backgroundColor: SERIES[i % SERIES.length],
      data: visible.map((g) => {
        const cell = g.cells.find((c) => c.value === col);
        return cell && !cell.suppressed ? cell.pct : 0;
      }),
    })),
  }), [visible, columns]);

  const chartOpts = {
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } },
    scales: {
      x: { stacked: true },
      y: { stacked: true, max: 100, ticks: { callback: (v) => `${v}%` } },
    },
  };

  // "Clinical: 62% Rehost, 21% Replatform" — the sentence a consultant says.
  const sentence = (g) => {
    const shown = g.cells.filter((c) => !c.suppressed && c.pct > 0).slice(0, 2);
    if (!shown.length) return 'No cell in this row clears the cohort minimum.';
    return `${g.key}: ${shown.map((c) => `${c.pct}% ${c.value}`).join(', ')}`;
  };

  const onExport = () => {
    exportCsv(
      `primordia-benchmarks-${dim.key}-${meas.key}.csv`,
      visible,
      [
        { label: dim.label, value: 'key' },
        { label: 'Clients (n)', value: 'clients' },
        { label: 'Applications (n)', value: 'apps' },
        ...columns.map((col) => ({
          label: `${col} %`,
          value: (g) => {
            const cell = g.cells.find((c) => c.value === col);
            if (!cell) return '';
            return cell.suppressed ? 'insufficient data' : cell.pct;
          },
        })),
      ]
    );
  };

  return (
    <Box>
      <SectionHeading
        icon={<InsightsIcon sx={{ color: 'sectionHeader.main' }} />}
        title={`${meas.label} by ${dim.label.toLowerCase()}`}
        subtitle="How comparable estates actually landed. Percentages are of applications within the row; any cell backed by too few clients is withheld and excluded from the chart, so a bar may total under 100%."
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField select size="small" label="Dimension" value={dimKey} onChange={(e) => setDimKey(e.target.value)} sx={{ minWidth: 200 }}>
          {DIMENSIONS.map((d) => <MenuItem key={d.key} value={d.key}>{d.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Measure" value={measKey} onChange={(e) => setMeasKey(e.target.value)} sx={{ minWidth: 220 }}>
          {MEASURES.map((m) => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={onExport} disabled={!visible.length}>
          Export aggregates (CSV)
        </Button>
      </Stack>

      {!visible.length ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No {dim.label.toLowerCase()} clears the {minCohort}-client minimum for {meas.label.toLowerCase()} yet. Lower the
          bar only if you can defend it, or wait until more engagements land.
        </Alert>
      ) : (
        <Paper sx={{ p: 2, mb: 2, height: 340 }}>
          <Box sx={{ height: 300 }}><Bar data={chart} options={chartOpts} /></Box>
        </Paper>
      )}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
              <TableCell>{dim.label}</TableCell>
              <TableCell>Cohort</TableCell>
              {columns.map((c) => <TableCell key={c} align="right">{c}</TableCell>)}
              <TableCell>What to say</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((g) => (
              <TableRow key={g.key} hover>
                <TableCell><b>{g.key}</b></TableCell>
                <TableCell><Cohort clients={g.clients} apps={g.apps} /></TableCell>
                {columns.map((col) => {
                  const cell = g.cells.find((c) => c.value === col);
                  if (!cell) return <TableCell key={col} align="right"><span style={{ color: '#ccc' }}>—</span></TableCell>;
                  return (
                    <TableCell key={col} align="right">
                      {cell.suppressed ? (
                        <Insufficient clients={cell.clients} minCohort={minCohort} />
                      ) : (
                        <Tooltip title={`${plural(cell.apps, 'application')} across ${plural(cell.clients, 'client')}`}>
                          <Box component="span" sx={{ fontWeight: 600, color: HEAD }}>{cell.pct}%</Box>
                        </Tooltip>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell sx={{ color: GREY }}>{sentence(g)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {withheld.length > 0 && (
        <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
          {plural(withheld.length, 'further value')} of {dim.label.toLowerCase()} appear in the corpus but are seen at
          fewer than {minCohort} clients. They are withheld entirely — including their names, since a rare value plus a
          statistic identifies the client behind it.
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TAB 2 — Typical values: the "what to expect" reference rows.
// ---------------------------------------------------------------------------
function typicalRows(rows, groupOf, minCohort) {
  const groups = new Map();
  rows.forEach((r) => {
    const g = norm(groupOf(r));
    if (!has(g)) return;
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g).push(r);
  });
  return [...groups.entries()]
    .map(([key, members]) => ({
      key,
      apps: members.length,
      clients: new Set(members.map((m) => m.tenantId)).size,
      rto: statFor(members, rtoOf, { kind: 'median', options: PICKLISTS.rto, minCohort }),
      rpo: statFor(members, rpoOf, { kind: 'median', options: PICKLISTS.rpo, minCohort }),
      hosting: statFor(members, hostingOf, { minCohort }),
      disposition: statFor(members, sixROf, { minCohort }),
    }))
    .filter((r) => r.clients >= minCohort)
    .sort((a, b) => b.apps - a.apps || a.key.localeCompare(b.key));
}

function TypicalTable({ label, rows, minCohort }) {
  const quote = (r) => {
    const parts = [];
    if (!r.rto.suppressed && r.rto.value) parts.push(`an RTO around ${r.rto.value}`);
    if (!r.rpo.suppressed && r.rpo.value) parts.push(`an RPO around ${r.rpo.value}`);
    if (!r.hosting.suppressed && r.hosting.value) parts.push(`${r.hosting.value} hosting today (${r.hosting.share}%)`);
    if (!r.disposition.suppressed && r.disposition.value) parts.push(`a ${r.disposition.value} disposition (${r.disposition.share}%)`);
    if (!parts.length) return 'Nothing in this row clears the cohort minimum.';
    return `Expect ${parts.join(', ')}.`;
  };

  if (!rows.length) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No {label.toLowerCase()} is backed by {minCohort} or more clients yet, so no typical values are published.
      </Alert>
    );
  }

  return (
    <Paper sx={{ mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
            <TableCell>{label}</TableCell>
            <TableCell>Cohort</TableCell>
            <TableCell>Median RTO</TableCell>
            <TableCell>Median RPO</TableCell>
            <TableCell>Typical hosting</TableCell>
            <TableCell>Typical disposition</TableCell>
            <TableCell>What to expect</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key} hover>
              <TableCell><b>{r.key}</b></TableCell>
              <TableCell><Cohort clients={r.clients} apps={r.apps} /></TableCell>
              <TableCell><Stat stat={r.rto} minCohort={minCohort} /></TableCell>
              <TableCell><Stat stat={r.rpo} minCohort={minCohort} /></TableCell>
              <TableCell>
                <Stat stat={r.hosting} minCohort={minCohort} format={(s) => `${s.value} · ${s.share}%`} />
              </TableCell>
              <TableCell>
                <Stat stat={r.disposition} minCohort={minCohort} format={(s) => `${s.value} · ${s.share}%`} />
              </TableCell>
              <TableCell sx={{ color: GREY, minWidth: 260 }}>{quote(r)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function TypicalTab({ rows, minCohort }) {
  const byType = useMemo(() => typicalRows(rows, appTypeOf, minCohort), [rows, minCohort]);
  const byTier = useMemo(() => typicalRows(rows, drTierOf, minCohort), [rows, minCohort]);

  return (
    <Box>
      <SectionHeading
        icon={<InsightsIcon sx={{ color: 'sectionHeader.main' }} />}
        title="Typical values — reference rows for a workshop"
        subtitle="Median for the ordinal buckets (RTO, RPO), most common value for hosting and disposition. Quote the row, then quote the cohort next to it: a benchmark without its n is an opinion."
      />
      <Typography variant="overline" sx={{ color: GREY }}>By application type</Typography>
      <Box sx={{ mt: 0.5 }}>
        <TypicalTable label="Application type" rows={byType} minCohort={minCohort} />
      </Box>
      <Typography variant="overline" sx={{ color: GREY }}>By DR tier</Typography>
      <Box sx={{ mt: 0.5 }}>
        <TypicalTable label="DR tier" rows={byTier} minCohort={minCohort} />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TAB 3 — Vendor intelligence: the most quotable material in a discovery call.
// ---------------------------------------------------------------------------
function VendorTab({ rows, minCohort }) {
  const { shown, withheldVendors, withheldApps } = useMemo(() => {
    const groups = new Map();
    rows.forEach((r) => {
      const v = vendorOf(r);
      if (!has(v)) return;
      if (!groups.has(v)) groups.set(v, []);
      groups.get(v).push(r);
    });

    const all = [...groups.entries()].map(([vendor, members]) => ({
      vendor,
      apps: members.length,
      clients: new Set(members.map((m) => m.tenantId)).size,
      hosting: statFor(members, hostingOf, { minCohort }),
      disposition: statFor(members, sixROf, { minCohort }),
      sso: rateFor(members, ssoOf, (v) => v === 'Available', minCohort),
      completion: Math.round(
        members.reduce((a, m) => a + (m.completion || 0), 0) / (members.length || 1)
      ),
    }));

    const pass = all.filter((v) => v.clients >= minCohort)
      .sort((a, b) => b.clients - a.clients || b.apps - a.apps || a.vendor.localeCompare(b.vendor));
    const fail = all.filter((v) => v.clients < minCohort);
    return {
      shown: pass,
      withheldVendors: fail.length,
      withheldApps: fail.reduce((a, v) => a + v.apps, 0),
    };
  }, [rows, minCohort]);

  const onExport = () => {
    exportCsv('primordia-benchmarks-vendors.csv', shown, [
      { label: 'Vendor', value: 'vendor' },
      { label: 'Clients (n)', value: 'clients' },
      { label: 'Applications (n)', value: 'apps' },
      { label: 'Typical hosting', value: (v) => (v.hosting.suppressed ? 'insufficient data' : v.hosting.value) },
      { label: 'Typical disposition', value: (v) => (v.disposition.suppressed ? 'insufficient data' : v.disposition.value) },
      { label: 'SSO available %', value: (v) => (v.sso.suppressed ? 'insufficient data' : v.sso.value) },
      { label: 'Avg. data completion %', value: 'completion' },
    ]);
  };

  return (
    <Box>
      <SectionHeading
        icon={<InsightsIcon sx={{ color: 'sectionHeader.main' }} />}
        title="Vendor intelligence across engagements"
        subtitle="Only vendors seen at the cohort minimum or more appear here. A vendor running at one or two clients is effectively a name-tag for those clients, so it is left out entirely — name included."
      />
      <Stack direction="row" sx={{ mb: 2 }} alignItems="center">
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={onExport} disabled={!shown.length}>
          Export aggregates (CSV)
        </Button>
      </Stack>

      {!shown.length ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No vendor is currently deployed at {minCohort} or more clients in the corpus, so nothing can be published here.
        </Alert>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                <TableCell>Vendor</TableCell>
                <TableCell>Cohort</TableCell>
                <TableCell>Typical hosting</TableCell>
                <TableCell>Typical disposition</TableCell>
                <TableCell>SSO available</TableCell>
                <TableCell>Avg. data completion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((v) => (
                <TableRow key={v.vendor} hover>
                  <TableCell><b>{v.vendor}</b></TableCell>
                  <TableCell><Cohort clients={v.clients} apps={v.apps} /></TableCell>
                  <TableCell>
                    <Stat stat={v.hosting} minCohort={minCohort} format={(s) => `${s.value} · ${s.share}%`} />
                  </TableCell>
                  <TableCell>
                    <Stat stat={v.disposition} minCohort={minCohort} format={(s) => `${s.value} · ${s.share}%`} />
                  </TableCell>
                  <TableCell>
                    <Stat stat={v.sso} minCohort={minCohort} format={(s) => `${s.value}% of apps`} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: HEAD }}>{v.completion}%</Typography>
                    <Cohort clients={v.clients} apps={v.apps} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {withheldVendors > 0 && (
        <Typography variant="caption" sx={{ color: GREY, display: 'block', mt: 1 }}>
          {plural(withheldVendors, 'further vendor')} ({plural(withheldApps, 'application')}) are present in the corpus at
          fewer than {minCohort} clients and are withheld in full — no name, no statistic.
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------
export default function Benchmarks({ tenantId }) {
  // Master-only. In deployed environments the Cognito group is the authority;
  // with auth switched off (local dev) the master workspace stands in for it.
  const allowed = isMaster() || (!getConfig().authRequired && tenantId === MASTER_TENANT);

  const [apps, setApps] = useState([]);
  const [eligible, setEligible] = useState(null);
  const [excludedSamples, setExcludedSamples] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [minCohort, setMinCohort] = useState(3);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    let live = true;
    if (!allowed) {
      setLoaded(true);
      return () => { live = false; };
    }
    // 'optimum' is the master workspace, so this returns every tenant's apps.
    Promise.all([
      store.listFactSheets(MASTER_TENANT, { type: 'Application' }),
      store.listTenants(),
    ]).then(([rows, tenants]) => {
      if (!live) return;
      // Only REAL, active client engagements may form the cohort. Synthetic
      // tenants (kind 'sample') and the master workspace are excluded, because a
      // cohort minimum met by padding with fabricated data is not anonymity —
      // the reader could work backwards to an individual client. Archived
      // engagements are excluded too: a frozen client should stop contributing.
      const real = (tenants || []).filter(
        (t) => t && t.kind === 'client' && t.status !== 'archived'
      );
      setEligible(new Set(real.map((t) => t.id)));
      setExcludedSamples(
        (tenants || []).filter((t) => t && t.kind === 'sample').map((t) => t.name || t.id)
      );
      setApps(rows);
      setLoaded(true);
    });
    return () => { live = false; };
  }, [allowed]);

  // The corpus: real client engagements only. Fact sheets sitting in the master
  // workspace itself are internal scratch, not a client engagement.
  const corpus = useMemo(
    () => apps.filter(
      (a) => has(a.tenantId)
        && a.tenantId !== MASTER_TENANT
        // Until the roster loads, `eligible` is null — admit nothing rather than
        // everything, so a slow response can never briefly widen the cohort.
        && (eligible ? eligible.has(a.tenantId) : false)
    ),
    [apps, eligible]
  );
  const clientCount = useMemo(() => new Set(corpus.map((a) => a.tenantId)).size, [corpus]);
  const ownContribution = useMemo(
    () => (tenantId && tenantId !== MASTER_TENANT ? corpus.filter((a) => a.tenantId === tenantId).length : 0),
    [corpus, tenantId]
  );

  if (!allowed) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" icon={<LockIcon />}>
          <AlertTitle>Benchmarks are an Optimum-internal view</AlertTitle>
          Cross-client benchmarks pool anonymised, aggregate patterns from Optimum&rsquo;s discovery engagements and are
          visible only to Optimum master administrators. Nothing from your workspace is exposed to another client, and no
          other client&rsquo;s data is exposed to you — no application names, owners or infrastructure detail ever leave a
          workspace. Ask your Optimum engagement lead if you would like the relevant benchmarks walked through with you.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }} flexWrap="wrap">
        <InsightsIcon sx={{ color: 'sectionHeader.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Cross-Client Benchmarks</Typography>
        <HelpBubble topic="benchmarks" />
        <Chip size="small" label="Optimum-internal · aggregates only" sx={{ bgcolor: '#eef3ff', color: BLUE, fontWeight: 600 }} />
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          size="small"
          label="Minimum cohort"
          value={minCohort}
          onChange={(e) => setMinCohort(Number(e.target.value))}
          helperText="Clients required behind a figure"
          sx={{ minWidth: 190 }}
        >
          {MIN_COHORT_OPTIONS.map((n) => (
            <MenuItem key={n} value={n}>{plural(n, 'client')}</MenuItem>
          ))}
        </TextField>
      </Stack>
      <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
        What Optimum has learned across every engagement, expressed as guidance you can quote: which dispositions
        comparable estates chose, what RTO and RPO a tier actually carries, and how a vendor behaves in the field. The
        minimum cohort can be raised but never lowered below {MIN_COHORT_OPTIONS[0]} clients.
        {ownContribution > 0 && ` The workspace you are currently viewing contributes ${plural(ownContribution, 'application')} to this corpus.`}
      </Typography>

      <PrivacyPanel minCohort={minCohort} clients={clientCount} apps={corpus.length} />

      {loaded && excludedSamples.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Sample workspaces are excluded from the cohort</AlertTitle>
          {excludedSamples.join(', ')} {excludedSamples.length === 1 ? 'is a demonstration workspace' : 'are demonstration workspaces'}{' '}
          and {excludedSamples.length === 1 ? 'does' : 'do'} not count toward the {minCohort}-client minimum. Padding a
          cohort with synthetic data would defeat the anonymity it exists to provide — the reader could work backwards
          to an individual client.
        </Alert>
      )}

      {loaded && clientCount < minCohort && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Corpus below the cohort minimum</AlertTitle>
          Only {plural(clientCount, 'client workspace')} currently hold application data, which is under the{' '}
          {minCohort}-client minimum. Every statistic below will read &ldquo;insufficient data&rdquo; until more
          engagements are loaded — by design, not by failure.
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Tab label={<TabLabel text="Patterns" topic="tabBenchPatterns" />} sx={{ fontWeight: 700, textTransform: 'none' }} />
        <Tab label={<TabLabel text="Typical values" topic="tabBenchTypicalValues" />} sx={{ fontWeight: 700, textTransform: 'none' }} />
        <Tab label={<TabLabel text="Vendor intelligence" topic="tabBenchVendorIntelligence" />} sx={{ fontWeight: 700, textTransform: 'none' }} />
      </Tabs>

      {tab === 0 && <PatternsTab rows={corpus} minCohort={minCohort} />}
      {tab === 1 && <TypicalTab rows={corpus} minCohort={minCohort} />}
      {tab === 2 && <VendorTab rows={corpus} minCohort={minCohort} />}
    </Box>
  );
}
